import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { cn } from "../../lib/utils.js";
import {
  FIELD_AFFIX_CLASS,
  fieldControlClass,
  fieldFrameClass,
  type FieldLookProps,
} from "../../lib/field-frame.js";
import { StepButtons } from "./StepButtons.js";
import { useNumberField, type NumberValue } from "./useNumberField.js";

/* Hides the browser's own spinners while the step buttons stand in for
   them, so the field never shows two sets. */
const NATIVE_SPINNERS_HIDDEN =
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

interface NumberInputOwnProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "value" | "onChange" | "size" | "prefix"
> {
  readonly label?: string;
  readonly hint?: string;
  readonly error?: string;
  readonly value: NumberValue;
  /** Sub-multiple for arrow-key/spinner steps. Defaults to `1`. */
  readonly step?: number;
  /** Inside the frame before the number (an icon, a currency sign). */
  readonly prefix?: ReactNode;
  /** Inside the frame after the number (a unit). A `Select
   *  variant="inline"` here is the value-plus-unit field; it stays its own
   *  tab stop. */
  readonly suffix?: ReactNode;
  /** Minus and plus buttons at the end of the frame. They step by `step`,
   *  clamp to `min`/`max`, disable at each bound and keep focus in the
   *  input: they are out of the tab order, and ArrowUp/ArrowDown still
   *  step. An empty field steps from 0. Hides the native spinners. */
  readonly stepButtons?: boolean;
}

/** One of the two, never both. */
type NumberInputValueProps =
  | {
      /** Called on every keystroke that parses, with the number or `""`. */
      readonly onChange: (value: NumberValue) => void;
      readonly onCommit?: never;
    }
  | {
      /** Draft mode: the typed text stays a draft (`type="text"`,
       *  `inputMode="decimal"`) and commits on blur or Enter when finite or
       *  empty; Escape reverts it. A non-finite draft never commits and
       *  marks the field invalid (`aria-invalid` and the frame). `text` is
       *  the exact typed decimal (trimmed): `Number()` drops digits past 15
       *  significant ones, so a caller storing DECIMAL text keeps `text`.
       *  A step button press commits at once; the arrow keys do not step. */
      readonly onCommit: (value: NumberValue, text: string) => void;
      readonly onChange?: never;
    };

/** `variant` / `size` are the field look (lib/field-frame.ts). The native
 *  numeric `size` attribute is omitted; width is layout, set by the parent.
 *  `variant="readonly"` also sets native `readOnly`; `readOnly` alone keeps
 *  the default look. */
export type NumberInputProps = NumberInputOwnProps &
  NumberInputValueProps &
  FieldLookProps;

/**
 * Number input. Internally renders a native `<input type="number">` but
 * coerces the value to `number | ''` (empty when the user cleared the field)
 * so downstream Zod schemas can decide whether to reject empty. With
 * `onCommit` instead of `onChange` it is a text field holding a draft
 * (useNumberField.ts).
 *
 * states-check-ignore: the focus treatment lives in the field-frame recipe
 * (lib/field-frame.ts) — the text-field look on the frame: focus-frame, or
 * focus-field on the ghost inline frame.
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  function NumberInput(
    {
      label,
      hint,
      error,
      value,
      onChange,
      onCommit,
      onFocus,
      onBlur,
      onKeyDown,
      step = 1,
      prefix,
      suffix,
      stepButtons = false,
      variant,
      size,
      className,
      id,
      disabled,
      readOnly,
      min,
      max,
      placeholder,
      ...rest
    },
    ref,
  ) {
    const autoId = useId();
    const inputId = id ?? autoId;
    /* Mirror Input.tsx and NativeSelect.tsx: tie hint/error to the control for
       assistive tech. This primitive rendered the paragraph without wiring it,
       so a number field's hint — and, once callers began passing `error`, its
       validation message — was visible but never announced. */
    const hasError = !!error;
    const description = hasError || hint ? `${inputId}-description` : undefined;
    const field = useNumberField({
      value,
      onChange,
      onCommit,
      onFocus,
      onBlur,
      onKeyDown,
    });
    const invalid = hasError || field.invalid;
    // A text field in draft mode: no native stepping or range attributes.
    const drafting = !!onCommit;
    return (
      <div className="w-full">
        {label ? (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-pb-text-secondary mb-1.5"
          >
            {label}
          </label>
        ) : null}
        <div
          className={fieldFrameClass({
            variant,
            size,
            invalid,
            disabled,
          })}
        >
          {prefix ? <span className={FIELD_AFFIX_CLASS}>{prefix}</span> : null}
          <input
            id={inputId}
            ref={ref}
            type={drafting ? "text" : "number"}
            inputMode="decimal"
            step={drafting ? undefined : step}
            min={drafting ? undefined : min}
            max={drafting ? undefined : max}
            disabled={disabled}
            readOnly={variant === "readonly" || readOnly}
            value={field.text}
            placeholder={placeholder}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={description}
            {...field.handlers}
            className={cn(
              fieldControlClass({ variant, size }),
              stepButtons && NATIVE_SPINNERS_HIDDEN,
              className,
            )}
            {...rest}
          />
          {suffix ? <span className={FIELD_AFFIX_CLASS}>{suffix}</span> : null}
          {stepButtons ? (
            <StepButtons
              value={field.stepBase}
              step={step}
              min={min}
              max={max}
              name={label ?? rest["aria-label"]}
              disabled={!!disabled || variant === "readonly" || !!readOnly}
              onStep={field.onStep}
            />
          ) : null}
        </div>
        {hint || hasError ? (
          <p
            id={description}
            className={cn(
              "text-dense-1 mt-1",
              error ? "text-pb-red-text" : "text-pb-text-muted",
            )}
          >
            {error ?? hint}
          </p>
        ) : null}
      </div>
    );
  },
);
